import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ContactOrganization, crmFormReference, OrganizationData } from '../../../types/types';
import { Header } from '../components/Header';
import { OrganizationProfile } from '../components/OrganizationProfile';
import { Container, MainCard, PageContainer } from '../../../styles/GlobalLayout';
import { Tabs } from '../../../components/Tabs/Tabs';
import cubesBg from '../../../assets/images/cubesBg.png';
import GridStackExample from '../../contacts/ContactOverview/DND/GridStackExample';
import { useGetOrganizationDetailsQuery } from '../../../api/organizationPortalApiSlice';
import { useGetContactOrganizationsQuery } from '../../../api/organizationApiSlice';
import { ContactInfoCard } from '../components/ContactInfoCard';
import OrganizationListing from './Organization/Listing';
import { authFetch } from '../../../utils/authFetch';
import { SkeletonCell } from '../../../components/SkeletonCell';
import FormFiller from '../../../components/FormBuilder/CustomForm/FormFiller';
import moment from 'moment';
import { selectAuth, setAuth } from '../../auth/authSlice';

type ContactData = {
    id?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    signedUrl?: string;
    contactRoles?: { id: number; role: { id: number; name: string } }[];
    organization?: { name?: string; image: string; industry: string };
    contactOrganizations?: ContactOrganization[];
    createdAt?: Date;
    cfr?: crmFormReference;
    source: string;
    createdBy: { firstName: string; lastName: string };
    account: { firstName: string; lastName: string };
    lastPasswordResetAt?: string;
};

type ContactFromOrganizationsResponse = {
    id: number;
    fullName?: string;
    email?: string;
    image?: string;
    signedUrl?: string;
    legacyId?: string | null;
    roles?: Array<{ id: number; name: string }>;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: { firstName?: string; lastName?: string } | null;
    lastModifiedByAccount?: { firstName?: string; lastName?: string } | null;
    lastActivity: {
        contactId: number;
        companyId: number;
        ipAddress: string;
        lastLoginAt: string;
    }
};

type ContactOrganizationsPayload = {
    contact?: ContactFromOrganizationsResponse;
};

const formatName = (person?: { firstName?: string; lastName?: string } | null) => {
    const fullName = `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim();
    return fullName || 'N/A';
};

const formatDate = (value?: string) => {
    if (!value) {
        return 'N/A';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString();
};

const ContactDetails: React.FC = () => {
    const dispatch = useDispatch();
    const auth = useSelector(selectAuth);
    const [searchParams, setSearchParams] = useSearchParams();
    const id = Number(searchParams.get('id'));
    const isEditingContact = searchParams.get('edit') === 'true';
    const { data: organizationDetailsData, isLoading, isFetching, refetch } = useGetOrganizationDetailsQuery(id, 'contact', { refetchOnMountOrArgChange: true });
    const canFetchContacts = Boolean(organizationDetailsData) && !isLoading && !isFetching;
    const [contactData, setContactData] = useState<ContactData>({} as ContactData);
    const [refreshToken, setRefreshToken] = useState(0);

    const setEditingMode = (isEditing: boolean) => {
        const nextParams = new URLSearchParams(searchParams);

        if (isEditing) {
            nextParams.set('edit', 'true');
        } else {
            nextParams.delete('edit');
        }

        setSearchParams(nextParams, { replace: true });
    };

    useEffect(() => {
        if (!canFetchContacts || Number.isNaN(id)) {
            return;
        }

        let cancelled = false;

        const fetchContactDetails = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_URL;

                if (!apiBase) {
                    return;
                }

                const response = await authFetch(`${apiBase}/portal/contacts/multiple?recordIds=${encodeURIComponent(String(id))}`);
                const json: any = await response.json();

                if (!cancelled) {
                    const record = Array.isArray(json) && json.length > 0 ? json[0] : {};
                    setContactData(record as ContactData);
                }
            } catch {
                if (!cancelled) {
                    setContactData({} as ContactData);
                }
            }
        };

        void fetchContactDetails();

        return () => {
            cancelled = true;
        };
    }, [canFetchContacts, id, refreshToken]);

    const [tabs, setTabs] = useState([
        { id: 1, title: 'Contact Overview', active: true },
        { id: 2, title: 'Organizations', active: false },
    ]);

    const handleChangeTabs = (tabId: number) => {
        setTabs((prevTabs) =>
            prevTabs.map((tab) => ({
                ...tab,
                active: tab.id === tabId,
            }))
        );
    };

    const activeTabTitle = useMemo(
        () => tabs.find((tab) => tab.active)?.title ?? 'Contact Overview',
        [tabs]
    );

    const { data: contactOrganizationsData, isLoading: organizationsLoading, isFetching: organizationsFetching, refetch: refetchContactOrganizations } = useGetContactOrganizationsQuery(
        { page: 1, perPage: 10, id },
        { skip: !id, refetchOnMountOrArgChange: true }
    );

    const contactMeta = (contactOrganizationsData as ContactOrganizationsPayload | undefined)?.contact;
    const roleNames = Array.isArray(contactMeta?.roles)
        ? (contactMeta?.roles ?? []).map((role) => role.name).filter(Boolean)
        : [];
    const isProfileLoading = isLoading || isFetching || organizationsLoading || organizationsFetching;

    const refreshMe = async () => {
        const apiBase = process.env.REACT_APP_API_URL;
        if (!apiBase) {
            return;
        }

        try {
            const response = await authFetch(`${apiBase}/portal/me`);
            const json: any = await response.json();

            if (!response.ok) {
                return;
            }

            const user = json?.data ?? json;
            const resolvedOrganizationId = user?.primaryOrganization?.id ?? auth?.organizationId ?? null;

            const nextAuth = {
                ...auth,
                email: user?.email ?? auth?.email ?? null,
                userId: user?.id ?? auth?.userId ?? null,
                firstName: user?.firstName ?? auth?.firstName ?? null,
                lastName: user?.lastName ?? auth?.lastName ?? null,
                fullName: user?.fullName ?? auth?.fullName ?? null,
                organizationId: resolvedOrganizationId,
            };

            dispatch(setAuth(nextAuth as any));
        } catch {
            // ignore /me refresh errors
        }
    };

    const organizationData: OrganizationData = {
        info: {
            name: contactMeta?.fullName || 'N/A',
            email: contactMeta?.email || 'N/A',
            website: 'N/A',
            organizationId: String(contactMeta?.id || 'N/A'),
            logoUrl: contactMeta?.signedUrl,
            roles: roleNames,
        },
        about: {
            industry: "",
            owner: "",
            region: "",
            employeeRange: "",
        },
        creation: {
            createdBy: formatName(contactMeta?.createdBy),
            creationDate: contactMeta?.createdAt ? (moment(contactMeta.createdAt).isValid() ? moment(contactMeta.createdAt).format('MMMM DD, YYYY') : 'N/A') : 'N/A',
            lastModifiedBy: formatName(contactMeta?.lastModifiedByAccount),
            lastModifiedDate: contactMeta?.updatedAt ? (moment(contactMeta.updatedAt).isValid() ? moment(contactMeta.updatedAt).format('MMMM DD, YYYY') : 'N/A') : 'N/A',
        },
    };

    // When editing in inline mode, show only the form on the page
    if (id && isEditingContact) {
        return (
            <Container>
                <PageContainer>
                    <FormFiller
                        inlineMode
                        includeMetadata
                        hideCloseButton
                        hideFormTitle
                        hidePagesTitles
                        hideOrganizationsSection
                        formType="contact"
                        entityId={id}
                        onClose={() => setEditingMode(false)}
                        onSuccess={() => {
                            setEditingMode(false);
                            refetch();
                            setRefreshToken((prev) => prev + 1);
                            refetchContactOrganizations();
                            void refreshMe();
                        }}
                        allowFormSwitch={false}
                    />
                </PageContainer>
            </Container>
        );
    }

    return (
        <Container>
            <PageContainer>
                <Header
                    title="Contact Details"
                    subtitle="Manage your profile and account related settings here"
                    onClick={() => setEditingMode(true)}
                    btnTitle="Edit Contact Details"
                />

                <MainCard
                    style={{
                        backgroundImage: `url(${cubesBg})`,
                        backgroundSize: 'contain',
                        backgroundPosition: '0',
                        borderRadius: 12,
                        backgroundRepeat: 'no-repeat',
                    }}
                >
                    {isProfileLoading ? (
                        <div className='d-flex justify-content-between gap-3 pb-4'>
                            <div style={{ display: 'flex', gap: '24px', width: 'calc(100% - 306px)' }}>
                                <SkeletonCell height='120px' width='120px' rounded />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                                    <SkeletonCell height='24px' width='55%' />
                                    <SkeletonCell height='18px' width='45%' />
                                    <SkeletonCell height='18px' width='35%' />
                                    <SkeletonCell height='28px' width='70%' />
                                </div>
                            </div>

                            {/* <div style={{ width: '306px', background: 'rgba(255, 255, 255, 0.8)', borderRadius: 12, padding: 16 }}>
                                <SkeletonCell height='18px' width='55%' style={{ marginBottom: 14 }} />
                                <SkeletonCell height='16px' width='75%' style={{ marginBottom: 10 }} />
                                <SkeletonCell height='16px' width='70%' style={{ marginBottom: 10 }} />
                                <SkeletonCell height='16px' width='65%' style={{ marginBottom: 10 }} />
                                <SkeletonCell height='16px' width='60%' />
                            </div> */}
                        </div>
                    ) : (
                        <div className='d-flex justify-content-between gap-3'>
                            <OrganizationProfile data={organizationData.info} />

                            <ContactInfoCard
                                width='306px'
                                tabFontSize='12px'
                                aboutUserItems={[
                                    { label: 'Created By ', value: organizationData.creation.createdBy },
                                    { label: 'Creation Date ', value: organizationData.creation.creationDate },
                                    { label: 'Last Modified By ', value: organizationData.creation.lastModifiedBy },
                                    { label: 'Last Modified Date ', value: organizationData.creation.lastModifiedDate },
                                ]}
                                lastLoginItems={[
                                    { label: 'Last Login', value: contactMeta?.lastActivity?.lastLoginAt ? (moment(contactMeta.lastActivity.lastLoginAt).isValid() ? moment(contactMeta.lastActivity.lastLoginAt).format('MMMM DD, YYYY') : 'N/A') : 'N/A' },
                                    { label: 'Login IP', value: contactMeta?.lastActivity?.ipAddress || 'N/A' },
                                ]}
                            />
                        </div>
                    )}

                    {!isProfileLoading ? <Tabs tabs={tabs} onChangeTabs={handleChangeTabs} mainClass='mt-3' /> : null}
                </MainCard>

                {activeTabTitle === 'Contact Overview' && (
                    isLoading || isFetching ? (
                        <div className='pt-3'>
                            <div style={{ display: 'grid', gap: '16px' }}>
                                <div style={{ background: 'white', borderRadius: 12, padding: 16 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                                        <SkeletonCell height='250px' width='100%' />
                                        <SkeletonCell height='250px' width='100%' />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='pt-3'>
                            <GridStackExample
                                scope='contact'
                                widgets={organizationDetailsData?.widgets || []}
                                setWidgets={() => undefined}
                                refetch={() => undefined}
                                widgetRefetch={refetch}
                                userDataRefetch={() => undefined}
                                isLoading={false}
                                data={contactData}
                                backupWidget={undefined}
                                organization={undefined}
                            />
                        </div>
                    )
                )}

                {activeTabTitle === 'Organizations' && (
                    <div className='pt-3'>
                        <OrganizationListing
                            contactOrganizationsData={contactOrganizationsData}
                            isLoading={organizationsLoading}
                            isFetching={organizationsFetching}
                        />
                    </div>
                )}
            </PageContainer>
        </Container>
    );
};

export default ContactDetails;
